<script setup lang="ts">
import type { FormKitNode } from "@formkit/core";
import { FormKit } from "@formkit/vue";
import type { CountryCode } from "libphonenumber-js";
import parsePhoneNumber, {
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { v4 as uuid } from "uuid";

const { countryAbrOptions } = await useCountryAbrOptions();
const props = defineProps({
  phone: String,
  label: {
    type: String,
    default: "Phone",
  },
  error: {
    type: String,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: "Cellphone Number",
  },
  invalidMessage: {
    type: String,
    default: "Please enter a valid cellphone number",
  },
});

const uuidKey = uuid();

const phone = toRef(props, "phone");

const emits = defineEmits<{
  (e: "update:phone", value: string): void;
}>();

const form = ref<{
  phone: string;
  code: CountryCode;
  formatted: string;
  formattedNum: string;
  isValid: boolean;
}>({
  phone: "",
  code: "US",
  formatted: "(###) ###-####",
  formattedNum: "",
  isValid: false,
});

let isParsingPhone = false;

watch(
  phone,
  (phoneVal) => {
    if (!phoneVal) {
      return;
    }
    isParsingPhone = true;
    form.value.phone = "+" + String(phoneVal);
    const currentPhone = parsePhoneNumberFromString(form.value.phone, form.value.code);
    if (currentPhone) {
      form.value.code = currentPhone?.country ?? form.value.code;
      form.value.phone = currentPhone?.nationalNumber;
    }
    nextTick(() => {
      isParsingPhone = false;
    });
  },
  { immediate: true },
);

watch([() => form.value.code, countryAbrOptions], ([code, options]) => {
  if (!options.length) {
    return;
  }
  const match = options.find(
    (option: { value: string; formatted: string }) => option.value === code,
  );
  if (match) {
    form.value.formatted = match.formatted;
  }
}, { immediate: true });

watch(
  () => form.value.code,
  (val) => {
    if (val && !isParsingPhone) {
      form.value.phone = "";
      // The field remounts empty without an input event, so the number it
      // held for the previous country has to be withdrawn here.
      emits("update:phone", "");
    }
  },
);

const onUpdated = (val: any) => {
  // Set Value Phone
  form.value.phone = val;
  // A cleared field is reported too, or an optional phone would keep submitting
  // the last valid number; partial numbers stay blocked by the validphone rule.
  if (!val) {
    form.value.formattedNum = "";
    form.value.isValid = false;
    emits("update:phone", "");
    return;
  }
  // Format and check number
  const newPhoneNumber = parsePhoneNumber(val, form.value.code);

  if (newPhoneNumber && newPhoneNumber.isValid()) {
    form.value.formattedNum = newPhoneNumber.number ?? null;
    form.value.isValid = true;
    emits("update:phone", form.value.formattedNum.replace("+", ""));
  } else {
    // Clear the formatted number and mark it as invalid
    form.value.formattedNum = "";
    form.value.isValid = false;
  }
};

function validphone(node: FormKitNode): boolean {
  const newPhoneNumber = parsePhoneNumber(
    node.value as string,
    form.value.code,
  );
  return newPhoneNumber?.isValid() ?? false;
}
const isFormValid = ref(true);

const handleValidation = (valid: boolean) => {
  isFormValid.value = valid;
};
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex gap-3">
      <InputsSelect
        @validation="handleValidation"
        :options="countryAbrOptions"
        label="Code"
        v-model="form.code"
        wrapper-class="!max-w-[150px] !min-w-[150px]"
      />
      <FormKit
        v-bind="$attrs"
        type="mask"
        :name="`phone_${uuidKey}`"
        :key="`${form.code}_${form.formatted}`"
        :mask="form.formatted"
        :label="label"
        :model-value="form.phone"
        :placeholder="placeholder"
        @input="onUpdated"
        show-mask="false"
        unmask-value="true"
        :validation-rules="{ validphone }"
        :validation-messages="{
          validphone: invalidMessage,
        }"
        :errors="error ? [error] : []"
        autocomplete="off"
      />
    </div>
  </div>
</template>

<style></style>
